import { ComponentProps, useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import CircleButton from "@components/buttons/CircleButton";
import ClientAvatar from "@components/ClientAvatar";
import ExpertAvatar from "@components/ExpertAvatar";
import ExpertMessageForm from "@components/forms/ExpertMessageForm";
import MessageForm from "@components/forms/MessageForm";
import AddRecipients from "@components/messaging/AddRecipients";
import ChatThread from "@components/messaging/ChatThread";
import { InboxContext } from "@components/messaging/InboxProvider";
import MessageFormDisabled from "@components/messaging/MessageFormDisabled";
import styles from "@components/messaging/Messages.module.css";
import BodyCopy from "@components/typography/BodyCopy";
import Heading2 from "@components/typography/Heading2";
import useContextSafely from "@hooks/useContextSafely";
import useGlobalOpener from "@hooks/useGlobalOpener";
import useMe from "@hooks/useMe";
import useMyClient from "@hooks/useMyClient";
import useMyExpert from "@hooks/useMyExpert";
import { OpenerTypeEnum } from "@typing/Enums";
import { ChatFragment, UserTypeEnum } from "@typing/Generated";
import { getChatUsers } from "@utils/modelUtils";
import { isMobileWeb, isNativeApp } from "@utils/platformUtils";
import { capitalizeFirstLetter } from "@utils/stringUtils";

type Props = Pick<
  ComponentProps<typeof MessageForm>,
  "resourceData" | "setResourceData" | "setWorksheetData" | "worksheetData"
> & {
  chat: ChatFragment;
};

const Chat = ({ chat }: Props) => {
  const intl = useIntl();
  const me = useMe();
  const myClient = useMyClient();
  const myExpert = useMyExpert();

  const { editingChat, expandedOpener, journey, setSelectedChat } = useContextSafely(InboxContext);

  const chatUsers = journey ? getChatUsers(me, chat, journey) : [];

  const [shouldAnimate, setShouldAnimate] = useState(false);

  const messageContentRef = useRef<HTMLIonContentElement>(null);

  useEffect(() => {
    if (messageContentRef.current) {
      messageContentRef.current.scrollToBottom();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const myExpertRole = journey?.expertRoles?.find(role => role.expert.id === myExpert?.id);
  const messagesOpener = useGlobalOpener(OpenerTypeEnum.MESSAGES);

  const handleMessageCreate = () => {
    setShouldAnimate(true);
    messageContentRef.current?.scrollToBottom();

    setTimeout(() => {
      setShouldAnimate(false);
    }, 2000);
  };

  return (
    <div className={`${styles.chatFab} ${isNativeApp() || isMobileWeb() ? styles.menuSpacing : ""}`}>
      {editingChat ? (
        <div className={styles.editingChatHeader}>
          <div className={styles.chatNewMessageHeader}>
            <div className={styles.chatNewMessage}>
              <div className={styles.chatGoBack}>
                <CircleButton
                  ariaLabelKey="accessibility.ariaLabels.goBack"
                  fill="clear"
                  icon="chevronLeft"
                  onClick={() => setSelectedChat(undefined)}
                  size="medium"
                />
              </div>
              <Heading2 bold={false}>
                <FormattedMessage id="pages.messages.newMessage" />
              </Heading2>
            </div>
            <div className={styles.close}>
              <CircleButton
                ariaLabelKey="accessibility.ariaLabels.close"
                color="gray-500"
                fill="clear"
                icon="times"
                onClick={messagesOpener.close}
                size="medium"
              />
            </div>
          </div>

          <AddRecipients chat={chat} />
        </div>
      ) : (
        <div className={styles.chatNewMessageHeader}>
          <div className={styles.chatHeader}>
            <div className={styles.controlChatUsers}>
              <div className={styles.chatGoBack}>
                <CircleButton
                  ariaLabelKey="accessibility.ariaLabels.goBack"
                  fill="clear"
                  icon="chevronLeft"
                  onClick={() => setSelectedChat(undefined)}
                  size="medium"
                />
              </div>
              <div className={styles.controlsAvatars}>
                {myClient ? (
                  <ClientAvatar
                    clientId={myClient.id}
                    color="primary-500"
                    firstName={myClient.firstName}
                    journeyId={journey?.id}
                    lastName={myClient.lastName}
                  />
                ) : myExpertRole ? (
                  <ExpertAvatar expert={myExpertRole.expert} />
                ) : (
                  <div className={styles.fakeAvatar} />
                )}
                {chatUsers.map((user, index) => (
                  <div className={styles.controlsAvatarContainer} key={user.id}>
                    <div
                      className={styles.controlsAvatar}
                      style={{
                        left:
                          myExpert && !myExpertRole
                            ? (chatUsers.length - index - 1) * 22
                            : (chatUsers.length - index) * 22,
                        zIndex: chatUsers.length + 1 - index
                      }}
                    >
                      {user.userType === UserTypeEnum.EXPERT && "expert" in user.avatarProps ? (
                        <ExpertAvatar {...user.avatarProps} />
                      ) : "firstName" in user.avatarProps ? (
                        <ClientAvatar {...user.avatarProps} clientId={user.id} journeyId={journey.id} />
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
              <div className={styles.hideMobile}>
                <Heading2 color="gray-500">
                  {(myExpert && !myExpertRole
                    ? []
                    : [capitalizeFirstLetter(intl.formatMessage({ id: "dictionary.you" }))]
                  )
                    .concat(chatUsers.map(user => user.name))
                    .join(", ")}
                </Heading2>
              </div>

              <div className={styles.showMobile + " " + styles.oneLine}>
                <BodyCopy bold color="gray-500">
                  {[capitalizeFirstLetter(intl.formatMessage({ id: "dictionary.you" }))]
                    .concat(chatUsers.map(user => user.name))
                    .join(", ")}
                </BodyCopy>
              </div>
            </div>
          </div>
          <div className="ion-hide-lg-down">
            <CircleButton
              ariaLabelKey="accessibility.ariaLabels.close"
              color="gray-500"
              fill="clear"
              icon={expandedOpener.isOpen ? "compress" : "expand"}
              onClick={expandedOpener.toggle}
              size="medium"
            />
            <CircleButton
              ariaLabelKey="accessibility.ariaLabels.close"
              color="gray-500"
              fill="clear"
              icon="times"
              onClick={messagesOpener.close}
              size="medium"
            />
          </div>
        </div>
      )}

      <ChatThread
        chat={chat}
        chatUsers={chatUsers}
        messageContentRef={messageContentRef}
        shouldAnimate={shouldAnimate}
      />

      <div className={styles.chatForm}>
        {journey.hasExpertAccess ? (
          <ExpertMessageForm chat={chat} contentRef={messageContentRef} onMessageCreate={handleMessageCreate} />
        ) : (
          <MessageFormDisabled journey={journey} />
        )}
      </div>
    </div>
  );
};

export default Chat;
